import { along, distance, feature, length } from "@turf/turf";
import * as Plotly from "plotly.js";
import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";
import { convertLineToGeometry } from "@nens/react-leaflet/utils";

import Plot from "../components/ThreediPlot";
import { setHoverCircleCoordinates } from "../state/map";
import {
  getInterpolatedDem,
  getInterpolatedGroundwater,
  getInterpolatedSurfacewater,
  getLine,
} from "../state/mapProfileLine";

import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import menuStyles from "./Menu.module.css";
import RightMenuHeader from "./RightMenuHeader";

import "react-loading-skeleton/dist/skeleton.css";

// import styles from "./ProfileInspector.module.css";
// import LoadingIndicatorVisual from "../components/LoadingIndicatorVisual";

const getInterpolateDistanceArray = (
  interpolationNumber: number,
  distanceInMeter: number
): number[] => {
  // |__|__|__| 4 values have 3 distances (underscsores) between them. Therefore the totalpoints - 1.
  const distanceDelta = distanceInMeter / (interpolationNumber - 1);
  return Array(interpolationNumber)
    .fill(0)
    .map((_, index) => distanceDelta * index);
};

const ProfileInspector: React.FC = () => {
  const dispatch = useDispatch();
  const interPolatedDem = useSelector(getInterpolatedDem);
  const interPolatedGroundwater = useSelector(getInterpolatedGroundwater);

  // Don't let the ground water go above the surface
  let correctedGroundwater = useMemo(
    () =>
      interPolatedGroundwater.map((groundwaterHeight, index) => {
        const demHeight = interPolatedDem[index];
        if (groundwaterHeight && demHeight && groundwaterHeight > demHeight) return demHeight;
        return groundwaterHeight;
      }),
    [interPolatedGroundwater, interPolatedDem]
  );

  const interpolatedSurfacewater = useSelector(getInterpolatedSurfacewater);
  // maybeline indicates that it might be a line, but might also be null
  // see its type defenition in mapProfileLine.ts
  const maybeLine = useSelector(getLine);

  const drawCircleOnLine = useCallback(
    (percentage: number) => {
      if (!maybeLine) return;
      const lineGeometry = convertLineToGeometry(maybeLine);
      const profileLength = length(feature(lineGeometry));
      const atLength = (percentage / 100) * profileLength;
      const feat = along(feature(lineGeometry), atLength);

      dispatch(setHoverCircleCoordinates(feat.geometry.coordinates as XYLocation));
    },
    [maybeLine, dispatch]
  );

  let distanceInMeter = 0;
  if (maybeLine) {
    maybeLine.forEach((xy, index) => {
      const nextXY = maybeLine[index + 1];
      if (!nextXY) return;
      distanceInMeter +=
        1000 *
        distance(
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Point",
              coordinates: xy,
            },
          },
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Point",
              coordinates: nextXY,
            },
          },
          { units: "kilometers" }
        );
    });
  }

  const distanceArray = getInterpolateDistanceArray(interPolatedDem.length, distanceInMeter);

  // Calculate the placeholder values for plotly that make sure that the fill behaves correctly
  // If groundwater is used the placeholder should be the lowest point of the groundwater
  // otherwise the placeholder should be the lowest point of the surface/dem
  // groundwater is for now never used
  const useGroundwater =
    interPolatedGroundwater.length > 1 && interPolatedGroundwater.some((value) => value !== null);
  const getMinimumHelper = (a: number | null, b: number | null) => {
    if (a === null) return b;
    if (b === null) return a;
    return Math.min(a, b);
  };
  const minimumSurface =
    interPolatedDem.length === 0 ? null : interPolatedDem.reduce(getMinimumHelper);
  const filteredSurfaceWater = interpolatedSurfacewater.map((value) => {
    if (minimumSurface === null) return value;
    else if (value === null) return null;
    else if (value > minimumSurface) return value;
    else return null;
  });
  let placeholderValues;
  if (interPolatedDem.length > 2 && interPolatedDem.some((value) => value !== null)) {
    if (useGroundwater) {
      const minimumGroundwater = correctedGroundwater.reduce(getMinimumHelper);
      placeholderValues = distanceArray.map((_) => minimumGroundwater && minimumGroundwater - 0.5);
    } else {
      placeholderValues = distanceArray.map((_) => minimumSurface);
      // if groundwater not set change it to null array
      correctedGroundwater = distanceArray.map((_) => null);
    }
  }

  const intl = useIntl();

  // below way of organizing caused typer errors, but would be preferable
  const surfaceWaterExceedMinimumValuePlaceholder = {
    // this placeholder is so that surfacewater does not exceed minimum value
    x: distanceArray,
    y: placeholderValues,
    type: "scatter",
    mode: "lines",
    fillcolor: "bdcee100",
    showlegend: false,
    hoverinfo: "skip",
    marker: {
      color: "bdcee100",
    },
  } as Plotly.ScatterData;
  const surfacewaterGraphDetails = {
    // surface water is placed here and not at the bottom because otherwise the tonexty feature behaves funky and colors the value between surface and surface water also if surface is higher then surface water
    // the decision to put surface here did however make it needed to also add a placeholder layer above this one
    x: distanceArray,
    y: filteredSurfaceWater,
    hovertemplate: `%{x:.2f}, %{y:.2f}`,
    type: "scatter",
    fill: "tonexty",
    // fill: 'tozeroy',
    mode: "lines",
    fillcolor: "#8DA9F5FF",
    name: intl.formatMessage({
      id: "profileLineInspecter.chartLabelSurfaceWater",
      defaultMessage: "Surface water",
    }),
    marker: {
      color: "#8DA9F5FF",
    },
  } as Plotly.ScatterData;
  const groundwaterMinimumValuePlaceholder = {
    // this placeholder is so that groundwater that is below zero is not drawn from zero to the negative value, but from lowest point to negative value
    // this solution did however cause a set of other problems (see comments above)
    x: distanceArray,
    y: placeholderValues,
    type: "scatter",
    mode: "lines",
    fillcolor: "bdcee100",
    showlegend: false,
    hoverinfo: "skip",
    marker: {
      color: "bdcee100",
    },
  } as Plotly.ScatterData;
  const groundwaterGraphDetails = {
    x: distanceArray,
    y: correctedGroundwater,
    hovertemplate: `%{x:.2f}, %{y:.2f}`,
    type: "scatter",
    fill: "tonexty",
    mode: "lines",
    fillcolor: "bdcee1ff",
    name: intl.formatMessage({
      id: "profileLineInspecter.chartLabelGroundwater",
      defaultMessage: "Groundwater",
    }),
    marker: {
      color: "bdcee1ff",
    },
  } as Plotly.ScatterData;
  const demGraphDetails = {
    x: distanceArray,
    y: interPolatedDem,
    hovertemplate: `%{x:.2f}, %{y:.2f}`,
    type: "scatter",
    fill: "tonexty",
    mode: "lines",
    fillcolor: "3AA232FF",
    name: intl.formatMessage({
      id: "profileLineInspecter.chartLabelSurface",
      defaultMessage: "Surface",
    }),
    marker: {
      color: "#3AA232FF",
    },
  } as Plotly.ScatterData;

  const plotlyDetails = useGroundwater
    ? [
        surfaceWaterExceedMinimumValuePlaceholder,
        surfacewaterGraphDetails,
        groundwaterMinimumValuePlaceholder,
        groundwaterGraphDetails,
        demGraphDetails,
      ]
    : [
        surfaceWaterExceedMinimumValuePlaceholder,
        surfacewaterGraphDetails,
        groundwaterMinimumValuePlaceholder,
        // groundwaterGraphDetails, // deliberately no groundwater
        demGraphDetails,
      ];

  return (
    <div
      className={`${CollapsableSidebarStyles.LeftMenu} ${CollapsableSidebarStyles.LeftMenuPadChildDivs}`}
    >
      <RightMenuHeader
        titleElement={
          <FormattedMessage id="profileLineInspecter.title" defaultMessage="Side view tool" />
        }
        parent={"PROFILE"}
      />
      <hr className={menuStyles.SeperatorLine} />
      <div>
        {interPolatedDem.length < 2 || interpolatedSurfacewater.length < 2 ? (
          // <div className={styles.LoadingBox }>
          //   <span>Loading...</span>
          //   <span><LoadingIndicatorVisual/></span>
          // </div>
          <Skeleton
            height={335}
            // next line commented out, instead use default basecolor
            // baseColor={getComputedStyle(document.documentElement)
            //   .getPropertyValue("--global--color-soft")}
            highlightColor={getComputedStyle(document.documentElement).getPropertyValue(
              "--header-sides-bg-color"
            )}
          />
        ) : (
          <Plot
            style={{
              position: "relative",
              display: "inline-block",
              border: "1px solid #707070",
              borderRadius: 5,
              overflow: "hidden",
            }}
            data={plotlyDetails}
            layout={{
              // autosize: true, // autosize seems not working, not troubleshooted yet
              width: 335, // width and size hardcoded, but could be retrieved from javascript (might be dynamic css)
              height: 335,
              margin: {
                l: 40,
                r: 10,
                b: 40,
                t: 0,
              },
              legend: {
                // the x 1 is somehow needed to move the legend to the right. No idea why
                x: 1,
                xanchor: "right",
                // the y 100 is done to move the legend up to above the chart
                y: 100,
              },
              xaxis: {
                title: {
                  text: intl.formatMessage({
                    id: "profileLineInspecter.chartXAxisDistance",
                    defaultMessage: "Distance (m) &#8594;",
                  }),
                  standoff: 5,
                },
              },
              yaxis: {
                title: {
                  text: intl.formatMessage({
                    id: "profileLineInspecter.chartYAxisHeight",
                    defaultMessage: "Level (mMSL) &#8594;",
                  }),
                  standoff: 10,
                },
                exponentformat: "power",
                automargin: true,
              },
            }}
            onHover={(e) => {
              // @ts-ignore because xaxes does not exist in Plotly's type defs currently
              const total: number = e.xaxes[0].range[1];
              const current = Number(e.xvals[0]);
              if (current) {
                const percentage = (current / total) * 100.0;
                drawCircleOnLine(percentage);
              }
            }}
            onUnhover={() => {
              dispatch(setHoverCircleCoordinates(null));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProfileInspector;
